import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, LogOut, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { BrandMark } from "@/components/AuthShell";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { StatCards } from "@/components/super/StatCards";
import { CustomersTable } from "@/components/super/CustomersTable";
import { StatsPanel } from "@/components/super/StatsPanel";
import { AuditLog } from "@/components/super/AuditLog";
import { BackupPanel } from "@/components/super/BackupPanel";
import { UsersSection } from "@/components/super/UsersSection";
import { AnlopPortsSection } from "@/components/super/AnlopPortsSection";
import { PoweredBy } from "@/components/PoweredBy";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const H = ({ children, id }) => <h2 id={id} className="font-display text-xl font-semibold text-slate-900 mb-4 scroll-mt-24">{children}</h2>;

export default function SuperAdmin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [overview, setOverview] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [statsFor, setStatsFor] = useState("all");
  const [auditKey, setAuditKey] = useState(0);
  const [pwOpen, setPwOpen] = useState(false);
  const backupId = new URLSearchParams(window.location.search).get("backup");

  useEffect(() => {
    if (backupId) { setTimeout(() => document.getElementById("backup")?.scrollIntoView({ behavior: "smooth" }), 400); }
  }, [backupId]);

  const reload = useCallback(async () => {
    try {
      const [o, c] = await Promise.all([api.get("/admin/overview"), api.get("/admin/customers", { params: { include_deleted: showDeleted } })]);
      setOverview(o.data);
      setCustomers(c.data);
      setAuditKey((k) => k + 1);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [showDeleted]);

  useEffect(() => {
    reload();
  }, [reload]);

  const onStats = (cid) => {
    setStatsFor(cid);
    document.getElementById("statistikk")?.scrollIntoView({ behavior: "smooth" });
  };
  const active = customers.filter((c) => !c.deleted);

  return (
    <div className="min-h-screen" data-testid="super-admin-page">
      <header className="sticky top-0 z-30 bg-[#0A192F] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <BrandMark dark />
            <span className="hidden sm:inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-300"><ShieldCheck className="w-3.5 h-3.5" />Super Admin</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="hidden md:block text-sm text-slate-300 mr-2">{user.email}</span>
            <Button variant="ghost" size="sm" className="text-slate-200 hover:text-white hover:bg-white/10" onClick={() => setPwOpen(true)} data-testid="super-change-password"><KeyRound className="w-4 h-4" /></Button>
            <Button variant="ghost" size="sm" className="text-slate-200 hover:text-white hover:bg-white/10" data-testid="super-logout"
              onClick={async () => { await logout(); nav("/superadmin/login"); }}><LogOut className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Logg ut</span></Button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 lg:py-12 space-y-12">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Oversikt</h1>
          <p className="text-slate-500 mt-1 mb-6">Status for alle havnekalkulatorer på plattformen.</p>
          <StatCards data={overview} />
        </div>
        <section><H>Kunder</H>
          <CustomersTable customers={customers} showDeleted={showDeleted} setShowDeleted={setShowDeleted} reload={reload} onStats={onStats} />
        </section>
        <section><H id="havner">Anløpsliste – havner per kunde</H>
          <AnlopPortsSection refreshKey={auditKey} />
        </section>
        <section><H id="brukere">Brukere</H>
          <UsersSection refreshKey={auditKey} onChanged={reload} />
        </section>
        <section><H id="statistikk">Statistikk</H>
          <StatsPanel customers={active} selected={statsFor} setSelected={setStatsFor} />
        </section>
        <section><H id="backup">Backup</H>
          <BackupPanel highlight={backupId} onRestored={reload} customers={customers} />
        </section>
        <section>
          <AuditLog customers={customers} refreshKey={auditKey} onReverted={reload} />
        </section>
        <PoweredBy className="border-t border-slate-200 pt-5" />
      </main>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}
